import RestClientService from "@/core/services/restClient.service";
import { ICoreResponse } from "@/core/interfaces/coreResponse.interface";
import {
  ORGANIZATION_API_PATH,
  WHATSAPP_API_PATH,
} from "@/modules/Organization/constants";
import { IOrganization } from "@/core/interfaces/organization.interface";
import {
  AddWhatsappMemberDto,
  ContentUpload,
  CreateOrganizationDto,
  UpdateOrganizationDto,
  AuthorizeWhatsppMemberOTPDto,
  StartDateEndDateDTO,
  IOrganizationDocuments,
} from "@/modules/Organization/dtos/organization.dto";
import {
  UpdateOrganizationPreferenceRequest,
  updateOrganizationPreferenceRequestAsMultipartRequestData,
} from "@/modules/Organization/models/UpdateOrganizationPreferenceRequest";
import { formatString } from "@/core/utils/helpers";
import {
  AddAgentDto,
  BulkImportUsersRequest,
} from "@/modules/Organization/dtos/agent.dto";
import { UpdateWebsiteConfigDto } from "../dtos/website.dto";
import { IWebsiteContent } from "@/core/interfaces/website.interface";
import { IPassenger } from "@/core/interfaces/passenger.interface";
import { OrganizationProductivityParamsRequestModel } from "../models/FetchOrganizationProductivityParams.request";
import { UpdateFinancialProfileProviders } from "../models/UpdateFinancialProfileProviders.request";
import { DEPARTMENT_API_PATHS } from "@/modules/Department/constants";
import { COST_CENTER_PATH } from "@/modules/CostCenter/constants";
import { EMPLOYEE_GRADES_API_PATHS } from "@/modules/EmployeeGrade/constants";

class OrganizationService {
  private restClient: RestClientService;

  constructor() {
    this.restClient = new RestClientService();
  }

  public getFormDetails(): Promise<ICoreResponse> {
    return this.restClient.get(ORGANIZATION_API_PATH.ORGANIZATION_FORM);
  }

  public getAll(): Promise<ICoreResponse<IOrganization[]>> {
    return this.restClient.get(ORGANIZATION_API_PATH.GET);
  }

  public getAllByCreationDate(
    payload: StartDateEndDateDTO,
  ): Promise<ICoreResponse<IOrganization[]>> {
    return this.restClient.get(ORGANIZATION_API_PATH.GET, payload);
  }

  public getById(
    id: string,
    params: any,
  ): Promise<ICoreResponse<IOrganization>> {
    return this.restClient.get(
      `${ORGANIZATION_API_PATH.GET}${id}/`,
      params || {},
    );
  }

  public create(body: CreateOrganizationDto): Promise<ICoreResponse> {
    return this.restClient.post(ORGANIZATION_API_PATH.POST, body);
  }

  public createAgent(body: AddAgentDto, orgId: string): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(ORGANIZATION_API_PATH.ADD_AGENT_IN_ORGANIZATION, orgId),
      body,
    );
  }

  public validateBulkUsers(
    body: BulkImportUsersRequest,
    orgId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(
        ORGANIZATION_API_PATH.VALIDATE_BULK_ORGANIZATION_USERS,
        orgId,
      ),
      body,
    );
  }

  public importBulkUsers(
    body: BulkImportUsersRequest,
    orgId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(ORGANIZATION_API_PATH.IMPORT_BULK_ORGANIZATION_USERS, orgId),
      body,
    );
  }

  public update(body: UpdateOrganizationDto): Promise<ICoreResponse> {
    return this.restClient.patch(
      `${ORGANIZATION_API_PATH.PATCH}${body.public_id}/`,
      body,
    );
  }

  public delete(organizationId: string): Promise<ICoreResponse<any>> {
    return this.restClient.delete(ORGANIZATION_API_PATH.DELETE, organizationId);
  }

  public updatePreference(
    request: UpdateOrganizationPreferenceRequest,
    organizationId: string,
  ): Promise<ICoreResponse> {
    const formData =
      updateOrganizationPreferenceRequestAsMultipartRequestData(request);
    return this.restClient.post(
      `v1/organization/${organizationId}/organization-self-preference/update/`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
  }

  public getOrganizationById(organizationId: string): Promise<ICoreResponse> {
    return this.restClient.get(
      `${ORGANIZATION_API_PATH.GET_BY_ID}${organizationId}/`,
    );
  }

  public getOrganizationUsers(
    organizationId: string,
    params?: { page_no?: number; page_size?: number; search?: string },
    options?: { signal?: AbortSignal },
  ): Promise<ICoreResponse> {
    return this.restClient.get(
      formatString(
        ORGANIZATION_API_PATH.GET_ORGANIZATION_USERS,
        organizationId,
      ),
      params,
      { signal: options?.signal },
    );
  }

  public getOrganizationAgentById(
    organizationId: string,
    agentId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.get(
      formatString(
        ORGANIZATION_API_PATH.GET_ORGANIZATION_AGENT_BY_ID,
        organizationId,
        agentId,
      ),
    );
  }

  public getOrganizationDepartments(
    organizationId: string,
    isActive?: boolean,
  ): Promise<ICoreResponse> {
    const basePath = formatString(
      DEPARTMENT_API_PATHS.GET_DEPARTMENTS,
      organizationId,
    );
    const url =
      typeof isActive === "boolean"
        ? `${basePath}?is_active=${isActive}`
        : basePath;
    return this.restClient.get(url);
  }

  public updateWebsiteConfiguration(
    body: UpdateWebsiteConfigDto,
    orgId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(ORGANIZATION_API_PATH.WEBSITE_CONFIGURATION, orgId),
      body,
    );
  }

  public getWebsiteConfiguration(orgId: string): Promise<ICoreResponse> {
    return this.restClient.get(
      formatString(ORGANIZATION_API_PATH.WEBSITE_CONFIGURATION, orgId),
    );
  }

  public getWebsiteContent(slug: string): Promise<ICoreResponse> {
    return this.restClient.get(
      formatString(ORGANIZATION_API_PATH.WEBSITE_CONTENT, slug),
    );
  }

  public getOrganizationCostCenters(
    organizationId: string,
    isActive?: boolean,
  ): Promise<ICoreResponse> {
    const basePath = formatString(
      COST_CENTER_PATH.GET_COST_CENTERS,
      organizationId,
    );
    const url =
      typeof isActive === "boolean"
        ? `${basePath}?is_active=${isActive}`
        : basePath;
    return this.restClient.get(url);
  }

  public getOrganizationEmployeeGrades(
    organizationId: string,
    isActive?: boolean,
  ): Promise<ICoreResponse> {
    const basePath = formatString(
      EMPLOYEE_GRADES_API_PATHS.GET_EMPLOYEE_GRADES,
      organizationId,
    );
    const url =
      typeof isActive === "boolean"
        ? `${basePath}?is_active=${isActive}`
        : basePath;
    return this.restClient.get(url);
  }

  public updateWebsiteContent(
    body: IWebsiteContent,
    organizationId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.put(
      formatString(
        ORGANIZATION_API_PATH.WEBSITE_CONTENT_UPDATE,
        organizationId,
      ),
      body,
    );
  }

  public getOrganizationPassengers(
    organizationId: string,
    options?: {
      passengerIds?: string[];
      search?: string;
      signal?: AbortSignal;
    },
  ): Promise<ICoreResponse> {
    const params = new URLSearchParams();
    options?.passengerIds?.forEach((id) => params.append("passenger_ids", id));
    if (options?.search?.trim()) {
      params.set("search", options.search.trim());
    }

    const path = formatString(
      ORGANIZATION_API_PATH.GET_ORGANIZATION_PASSENGERS,
      organizationId,
    );
    const query = params.toString();

    return this.restClient.get(query ? `${path}?${query}` : path, undefined, {
      signal: options?.signal,
    });
  }

  public getOrganizationPassengerById(
    organizationId: string,
    passengerIds: string[],
  ): Promise<ICoreResponse> {
    return this.getOrganizationPassengers(organizationId, { passengerIds });
  }

  public getTravelerProfiles(
    organizationId: string,
    options?: {
      page?: number;
      pageSize?: number;
      search?: string;
      displayDocument?: string;
      passengerType?: string;
      signal?: AbortSignal;
    },
  ): Promise<ICoreResponse> {
    const params: Record<string, string | number> = {
      page: options?.page ?? 1,
      page_size: options?.pageSize ?? 20,
    };

    if (options?.search?.trim()) {
      params.search = options.search.trim();
    }

    if (options?.displayDocument?.trim()) {
      params.display_document = options.displayDocument.trim();
    }

    if (options?.passengerType?.trim()) {
      params.passenger_type = options.passengerType.trim();
    }

    return this.restClient.get(
      formatString(ORGANIZATION_API_PATH.GET_TRAVELER_PROFILES, organizationId),
      params,
      { signal: options?.signal },
    );
  }

  public getOrganizationPassengerSetupForm(
    organizationId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.get(
      formatString(
        ORGANIZATION_API_PATH.GET_ORGANIZATION_PASSENGER_SETUP_FORM,
        organizationId,
      ),
    );
  }

  public getOrganizationPassengerSetupFormV2(
    organizationId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.get(
      formatString(
        ORGANIZATION_API_PATH.GET_ORGANIZATION_PASSENGER_SETUP_FORM_V2,
        organizationId,
      ),
    );
  }

  public getReferenceOptions(url: string): Promise<ICoreResponse> {
    return this.restClient.get(url);
  }

  public updateOrganizationPassenger(
    body: IPassenger,
    organizationId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(
        ORGANIZATION_API_PATH.UPDATE_ORGANIZATION_PASSENGERS,
        organizationId,
      ),
      body,
    );
  }

  public createPassenger(
    body: IPassenger[],
    organizationId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(
        ORGANIZATION_API_PATH.POST_ORGANIZATION_PASSENGERS,
        organizationId,
      ),
      body,
    );
  }

  public deleteOrganizationPassengerById(
    organizationId: string,
    passengerId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.delete(
      formatString(
        ORGANIZATION_API_PATH.DELETE_ORGANIZATION_PASSENGERS,
        organizationId,
      ),
      passengerId,
    );
  }

  public organizationContentUpload(
    body: ContentUpload,
    organizationId: string,
  ): Promise<ICoreResponse> {
    const formdata = new FormData();
    const config = {
      headers: { "Content-Type": "multipart/form-data" },
    };

    formdata.append("content", body.content, body.content.name);
    formdata.append("content_category", body.content_category);
    formdata.append("content_sub_category", body.content_sub_category);
    body.content_type && formdata.append("content_type", body.content_type);

    return this.restClient.post(
      formatString(ORGANIZATION_API_PATH.CONTENT_UPLOAD, organizationId),
      formdata,
      config,
    );
  }

  public organizationConfigUpdate(
    body: IOrganizationDocuments,
    organizationId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(ORGANIZATION_API_PATH.CONFIG_UPDATE, organizationId),
      body,
    );
  }

  public getWhatsappMembers(
    organizationId: string,
    page = 1,
  ): Promise<ICoreResponse> {
    return this.restClient.get(
      formatString(WHATSAPP_API_PATH.GET, organizationId),
      { page },
    );
  }

  public addWhatsappMember(
    body: AddWhatsappMemberDto,
    organizationId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(WHATSAPP_API_PATH.POST, organizationId),
      body,
    );
  }

  public deleteWhatsappMembers(
    organizationId: string,
    whatsappMemberId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.delete(
      formatString(WHATSAPP_API_PATH.DELETE_WHATSAPP, organizationId),
      whatsappMemberId,
    );
  }

  public generateOTPForWhatsappMember(
    organizationId: string,
    whatsapp_member_id: string,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(
        WHATSAPP_API_PATH.GENERATE_OTP,
        organizationId,
        whatsapp_member_id,
      ),
      {},
    );
  }

  public authorizeOTPForWhatsappMember(
    body: AuthorizeWhatsppMemberOTPDto,
    organizationId: string,
    whatsapp_member_id: string,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(
        WHATSAPP_API_PATH.AUTHORIZE_OTP,
        organizationId,
        whatsapp_member_id,
      ),
      body,
    );
  }

  public getOrganizationStats(organizationId: string): Promise<ICoreResponse> {
    return this.restClient.get(
      formatString(ORGANIZATION_API_PATH.ORGANIZATION_SNAPSHOT, organizationId),
    );
  }

  public getOrganizationProductivity(
    organizationId: string,
    payload: OrganizationProductivityParamsRequestModel,
  ): Promise<ICoreResponse> {
    return this.restClient.get(
      formatString(
        ORGANIZATION_API_PATH.ORGANIZATION_PRODUCTIVITY,
        organizationId,
      ),
      payload,
    );
  }

  public getCurrencies(): Promise<ICoreResponse> {
    return this.restClient.get(ORGANIZATION_API_PATH.CURRENCIES);
  }

  public getAllSaasEliteOrganizations(): Promise<
    ICoreResponse<IOrganization[]>
  > {
    return this.restClient.get(ORGANIZATION_API_PATH.SAAS_ELITE_ORGANIZATION);
  }

  public updateEliteOrganizationStatus(
    organizationId: string,
    status: string,
  ): Promise<ICoreResponse<IOrganization[]>> {
    return this.restClient.post(
      formatString(
        ORGANIZATION_API_PATH.UPDATE_ELITE_ORGANIZATION_STATUS,
        organizationId,
      ),
      {
        status,
      },
    );
  }
  public getInternalProviders(): Promise<ICoreResponse> {
    return this.restClient.get(ORGANIZATION_API_PATH.GET_INTERNAL_PROVIDERS);
  }
  public updateFinancialProfileProviders(
    payload: UpdateFinancialProfileProviders,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      ORGANIZATION_API_PATH.UPDATE_INTERNAL_PROVIDERS,
      payload,
    );
  }
}

export default OrganizationService;
